/**
 * User Management Page
 *
 * RBAC administration: list internal users, change roles, update per-resource
 * permissions, deactivate. Owner-only for Owner/Admin changes; Admins can
 * manage User-tier accounts.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Shield, UserX, UserCheck, ChevronDown, ChevronUp, Crown, User as UserIcon,
  Lock, Mail, Calendar, UserPlus, X,
} from 'lucide-react';
import { Card } from '../components/ui-custom/ComplianceUI';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const LEVEL_LABELS = {
  none: 'None',
  read: 'Read',
  write: 'Read + Edit',
  delete: 'Read + Edit + Delete',
};
const LEVEL_COLORS = {
  none: '#6b7280',
  read: '#60a5fa',
  write: '#f59e0b',
  delete: '#10b981',
};

function RoleBadge({ role }) {
  const map = {
    owner: { icon: Crown, label: 'Owner', color: '#8b5cf6' },
    admin: { icon: Shield, label: 'Admin', color: '#2563eb' },
    user: { icon: UserIcon, label: 'User', color: '#6b7280' },
  };
  const conf = map[role] || map.user;
  const Icon = conf.icon;
  return (
    <span
      className="user-role-badge"
      style={{ background: `${conf.color}22`, color: conf.color, borderColor: `${conf.color}55` }}
    >
      <Icon size={12} /> {conf.label}
    </span>
  );
}

export default function UserManagementPage() {
  const { user: currentUser, isOwner, isAdminOrOwner } = useAuth();
  const [users, setUsers] = useState([]);
  const [resources, setResources] = useState([]);
  const [levels, setLevels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [primaryOwnerId, setPrimaryOwnerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [draftPerms, setDraftPerms] = useState({}); // user_id → permissions map
  const [savingId, setSavingId] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteDraft, setInviteDraft] = useState({ email: '', name: '', role: 'user' });
  const [inviting, setInviting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, rRes] = await Promise.all([
        fetch(`${API_URL}/api/users`, { credentials: 'include' }),
        fetch(`${API_URL}/api/users/resources`, { credentials: 'include' }),
      ]);

      if (uRes.status === 403) {
        toast.error("You don't have access to User Management");
        setLoading(false);
        return;
      }

      const uData = uRes.ok ? await uRes.json() : { users: [] };
      const rData = rRes.ok ? await rRes.json() : { resources: [], levels: [], roles: [] };

      setUsers(uData.users || []);
      setPrimaryOwnerId(uData.primary_owner_id || null);
      setResources(rData.resources || []);
      setLevels(rData.levels || []);
      setRoles(rData.roles || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const canEditTarget = useCallback(
    (u) => {
      // Nobody edits themselves on this surface
      if (u.id === currentUser?.id) return false;
      // Only Owner can edit another Owner or an Admin
      if ((u.role === 'owner' || u.role === 'admin') && !isOwner) return false;
      // Primary Owner is untouchable
      if (u.id === primaryOwnerId) return false;
      return isAdminOrOwner;
    },
    [currentUser, isOwner, isAdminOrOwner, primaryOwnerId]
  );

  const handleRoleChange = async (u, newRole) => {
    if (!window.confirm(`Change ${u.email}'s role to ${newRole}?`)) return;
    setSavingId(u.id);
    try {
      const res = await fetch(`${API_URL}/api/users/${u.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Role update failed');
        return;
      }
      toast.success(`${u.email} is now ${newRole}`);
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error('Role update error');
    } finally {
      setSavingId(null);
    }
  };

  const handleToggleActive = async (u) => {
    const action = u.is_active ? 'deactivate' : 'activate';
    if (!window.confirm(`${action} ${u.email}?`)) return;
    setSavingId(u.id);
    try {
      if (u.is_active) {
        const res = await fetch(`${API_URL}/api/users/${u.id}`, {
          method: 'DELETE', credentials: 'include',
        });
        if (!res.ok) { toast.error('Deactivation failed'); return; }
      } else {
        const res = await fetch(`${API_URL}/api/users/${u.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ is_active: true }),
        });
        if (!res.ok) { toast.error('Activation failed'); return; }
      }
      toast.success(`${u.email} ${action}d`);
      await fetchAll();
    } finally {
      setSavingId(null);
    }
  };

  const startEditPerms = (u) => {
    setExpandedId(expandedId === u.id ? null : u.id);
    if (expandedId !== u.id) {
      setDraftPerms({ ...draftPerms, [u.id]: { ...u.permissions } });
    }
  };

  const updateDraftPerm = (uid, resourceKey, level) => {
    setDraftPerms({
      ...draftPerms,
      [uid]: { ...(draftPerms[uid] || {}), [resourceKey]: level },
    });
  };

  const savePermissions = async (u) => {
    setSavingId(u.id);
    try {
      const res = await fetch(`${API_URL}/api/users/${u.id}/permissions`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ permissions: draftPerms[u.id] || {} }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
        return;
      }
      toast.success(`Permissions updated for ${u.email}`);
      setExpandedId(null);
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error('Save error');
    } finally {
      setSavingId(null);
    }
  };

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter(u => u.is_active).length,
    owners: users.filter(u => u.role === 'owner').length,
    admins: users.filter(u => u.role === 'admin').length,
    members: users.filter(u => u.role === 'user').length,
  }), [users]);

  if (!isAdminOrOwner) {
    return (
      <div className="user-management-page">
        <Card className="unauth-card">
          <Lock size={32} />
          <h2>Access denied</h2>
          <p>User Management is available to Owners and Administrators only.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="user-management-page" data-testid="user-management-page">
      <div className="page-header">
        <div>
          <h1>User Management</h1>
          <p>
            Grant and revoke access for your team. The primary Owner has permanent
            full control. Owners manage Admins and Users; Admins manage Users.
          </p>
        </div>
        <div className="user-stats">
          <div className="user-stat"><span className="stat-value">{stats.active}</span><span className="stat-label">Active</span></div>
          <div className="user-stat"><span className="stat-value">{stats.owners}</span><span className="stat-label">Owners</span></div>
          <div className="user-stat"><span className="stat-value">{stats.admins}</span><span className="stat-label">Admins</span></div>
          <div className="user-stat"><span className="stat-value">{stats.members}</span><span className="stat-label">Users</span></div>
        </div>
      </div>

      <div className="users-toolbar">
        <button
          className="btn-primary"
          onClick={() => setInviteOpen(true)}
          data-testid="invite-user-btn"
        >
          <UserPlus size={16} /> Invite user
        </button>
        <p className="toolbar-hint">
          Pre-provision a role for someone. On their first Google sign-in with that @eyvo.com address, they land with these permissions.
        </p>
      </div>

      {loading ? (
        <div className="users-loading">Loading…</div>
      ) : (
        <div className="users-list">
          {users.map((u) => {
            const editable = canEditTarget(u);
            const isPrimary = u.id === primaryOwnerId;
            const isMe = u.id === currentUser?.id;
            return (
              <Card key={u.id} className={`user-card ${u.is_active ? '' : 'inactive'}`} data-testid={`user-card-${u.email}`}>
                <div className="user-card-head">
                  <div className="user-identity">
                    {u.picture_url ? (
                      <img src={u.picture_url} alt="" className="user-avatar" />
                    ) : (
                      <div className="user-avatar-placeholder">{(u.name || u.email || '?')[0].toUpperCase()}</div>
                    )}
                    <div>
                      <div className="user-name">
                        {u.name || u.email}
                        {isMe && <span className="user-tag">(you)</span>}
                        {isPrimary && <span className="user-tag primary-owner">Primary Owner</span>}
                      </div>
                      <div className="user-email"><Mail size={12} /> {u.email}</div>
                      {u.last_login && (
                        <div className="user-meta"><Calendar size={11} /> Last login: {new Date(u.last_login).toLocaleString()}</div>
                      )}
                    </div>
                  </div>

                  <div className="user-actions">
                    <RoleBadge role={u.role} />
                    {editable && (
                      <>
                        <select
                          className="user-role-select"
                          value={u.role}
                          onChange={(e) => handleRoleChange(u, e.target.value)}
                          disabled={savingId === u.id}
                          data-testid={`role-select-${u.email}`}
                        >
                          {roles.map((r) => (
                            <option key={r} value={r} disabled={!isOwner && r !== 'user'}>
                              {r}
                            </option>
                          ))}
                        </select>
                        <button
                          className="btn-secondary btn-small"
                          onClick={() => startEditPerms(u)}
                          data-testid={`edit-perms-${u.email}`}
                        >
                          {expandedId === u.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          Permissions
                        </button>
                        <button
                          className={`btn-small ${u.is_active ? 'btn-danger' : 'btn-primary'}`}
                          onClick={() => handleToggleActive(u)}
                          data-testid={`toggle-active-${u.email}`}
                          disabled={savingId === u.id}
                        >
                          {u.is_active ? <><UserX size={14} /> Deactivate</> : <><UserCheck size={14} /> Activate</>}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {expandedId === u.id && (
                  <div className="user-perms-matrix">
                    <div className="perms-legend">
                      {levels.map((lvl) => (
                        <span key={lvl} className="perms-legend-item" style={{ color: LEVEL_COLORS[lvl] }}>
                          <span className="perms-dot" style={{ background: LEVEL_COLORS[lvl] }} />
                          {LEVEL_LABELS[lvl]}
                        </span>
                      ))}
                    </div>
                    <div className="perms-grid">
                      {resources.map((r) => {
                        const current = draftPerms[u.id]?.[r.key] ?? u.permissions?.[r.key] ?? 'none';
                        return (
                          <div key={r.key} className="perm-row" data-testid={`perm-row-${u.email}-${r.key}`}>
                            <span className="perm-label">{r.label}</span>
                            <div className="perm-buttons">
                              {levels.map((lvl) => (
                                <button
                                  key={lvl}
                                  className={`perm-btn ${current === lvl ? 'active' : ''}`}
                                  style={current === lvl ? {
                                    background: LEVEL_COLORS[lvl],
                                    borderColor: LEVEL_COLORS[lvl],
                                    color: '#fff',
                                  } : {}}
                                  onClick={() => updateDraftPerm(u.id, r.key, lvl)}
                                  data-testid={`perm-set-${u.email}-${r.key}-${lvl}`}
                                >
                                  {LEVEL_LABELS[lvl]}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="perms-save-row">
                      <button
                        className="btn-secondary"
                        onClick={() => { setExpandedId(null); }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-primary"
                        onClick={() => savePermissions(u)}
                        disabled={savingId === u.id}
                        data-testid={`save-perms-${u.email}`}
                      >
                        {savingId === u.id ? 'Saving…' : 'Save permissions'}
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Invite User Modal */}
      {inviteOpen && (
        <div className="modal-overlay" onClick={() => setInviteOpen(false)} data-testid="invite-modal">
          <div className="modal-content invite-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h2><UserPlus size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} /> Invite a team member</h2>
              <button className="modal-close" onClick={() => setInviteOpen(false)} data-testid="invite-close">
                <X size={20} />
              </button>
            </div>

            <div className="invite-form">
              <label>
                Email
                <input
                  type="email"
                  placeholder="jane.doe@eyvo.com"
                  value={inviteDraft.email}
                  onChange={(e) => setInviteDraft({ ...inviteDraft, email: e.target.value })}
                  data-testid="invite-email"
                  autoFocus
                />
                <small>Must be an @eyvo.com address.</small>
              </label>

              <label>
                Display name (optional)
                <input
                  type="text"
                  placeholder="Jane Doe"
                  value={inviteDraft.name}
                  onChange={(e) => setInviteDraft({ ...inviteDraft, name: e.target.value })}
                  data-testid="invite-name"
                />
              </label>

              <label>
                Role
                <select
                  value={inviteDraft.role}
                  onChange={(e) => setInviteDraft({ ...inviteDraft, role: e.target.value })}
                  data-testid="invite-role"
                >
                  <option value="user">User — per-resource permissions (default: read-only)</option>
                  {isOwner && <option value="admin">Admin — full CRUD, manages Users</option>}
                  {isOwner && <option value="owner">Owner — ultimate privilege</option>}
                </select>
                <small>
                  {isOwner
                    ? 'You can fine-tune resource-level permissions after they appear in the list.'
                    : 'Only Owners can create Admins or Owners.'}
                </small>
              </label>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setInviteOpen(false)}>Cancel</button>
              <button
                className="btn-primary"
                onClick={handleInvite}
                disabled={inviting || !inviteDraft.email.trim()}
                data-testid="invite-submit"
              >
                {inviting ? 'Sending…' : 'Send invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
